import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api';
const FONT = "'Inter', system-ui, -apple-system, sans-serif";
const HOURS = Array.from({ length: 24 }, (_, i) => i);

/* ─── SVG Icon engine ─────────────────────────────────── */
const Icon = ({ d, size = 18, color = 'currentColor', sw = 1.8 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"
        style={{ display: 'block', flexShrink: 0 }}>
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const IC = {
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    calendar: ['M8 2v4', 'M16 2v4', 'M3 10h18', 'M21 8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8z'],
    refresh: ['M23 4v6h-6', 'M1 20v-6h6', 'M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15'],
    truck: ['M1 3h15v13H1z', 'M16 8h4l3 3v5h-7V8z', 'M5.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z', 'M18.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    users: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
    car: ['M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.5 2.5C1.4 11.3 1 12.1 1 13v3c0 .6.4 1 1 1h2', 'M7 17a2 2 0 1 0 4 0 2 2 0 0 0-4 0', 'M15 17a2 2 0 1 0 4 0 2 2 0 0 0-4 0'],
    bus: ['M8 6v12', 'M16 6v12', 'M3 12h18', 'M3 6h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z'],
    driver: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M16 3.13a4 4 0 0 1 0 7.75', 'M21 21v-2a4 4 0 0 0-3-3.87'],
    check: 'M20 6L9 17l-5-5',
    chevronL: 'M15 18l-6-6 6-6',
    chevronR: 'M9 18l6-6-6-6',
};

const COLORS = {
    Available: { bg: 'linear-gradient(135deg,#4ade80,#22c55e)', border: '#86efac', shadow: 'rgba(34,197,94,.2)', text: '#15803d' },
    Daily: { bg: 'linear-gradient(135deg,#fb923c,#f97316)', border: '#fdba74', shadow: 'rgba(249,115,22,.25)', text: '#ea580c' },
    Monthly: { bg: 'linear-gradient(135deg,#a78bfa,#7c3aed)', border: '#c4b5fd', shadow: 'rgba(124,58,237,.25)', text: '#7c3aed' },
    Shuttle: { bg: 'linear-gradient(135deg,#60a5fa,#3b82f6)', border: '#93c5fd', shadow: 'rgba(59,130,246,.25)', text: '#1d4ed8' },
    'Shuttle with Booking': { bg: 'linear-gradient(135deg,#22d3ee,#06b6d4)', border: '#67e8f9', shadow: 'rgba(6,182,212,.25)', text: '#0891b2' },
    'Ad-hoc': { bg: 'linear-gradient(135deg,#fcd34d,#f59e0b)', border: '#fde68a', shadow: 'rgba(245,158,11,.25)', text: '#b45309' },
    Absent: { bg: 'linear-gradient(135deg,#f87171,#ef4444)', border: '#fca5a5', shadow: 'rgba(239,68,68,.25)', text: '#b91c1c' },
};

const TABS = [
    { key: 'fleet', label: 'Fleet', icon: 'car', color: '#4f46e5' },
    { key: 'drivers', label: 'Drivers', icon: 'user', color: '#0891b2' },
    { key: 'vfleet', label: 'Vendor Fleet', icon: 'bus', color: '#7c3aed' },
    { key: 'vdrivers', label: 'Vendor Drivers', icon: 'driver', color: '#0f766e' },
];

function fmtHour(h) {
    if (h === 0) return '12 AM';
    if (h < 12) return `${h} AM`;
    if (h === 12) return '12 PM';
    return `${h - 12} PM`;
}

function getTripAtHour(entityId, hour, trips) {
    return trips.find(t => {
        const ids = [
            t.vehicleId?._id || t.vehicleId,
            t.driverId?._id || t.driverId,
            t.vendorProposedVehicleId?._id || t.vendorProposedVehicleId,
            t.vendorProposedDriverId?._id || t.vendorProposedDriverId,
        ];
        if (!ids.includes(entityId)) return false;
        if (!['Scheduled', 'Ongoing'].includes(t.status)) return false;
        
        // Monthly trips occupy the whole day in the heatmap for clarity
        if (t.billingType === 'Monthly') return true;

        if (!t.startTime) return false;
        const [sh] = t.startTime.split(':').map(Number);
        const [eh] = (t.endTime || t.startTime).split(':').map(Number);
        return hour >= sh && hour <= (eh || sh);
    });
}

function getLeaveAtDate(entity, date) {
    if (!entity || !entity.leaves) return null;
    const target = new Date(date + 'T00:00:00');
    return entity.leaves.find(l => {
        if (l.status !== 'Approved' && l.status !== 'Active') return false;
        const start = new Date(l.startDate);
        const end = new Date(l.endDate);
        return target >= start && target <= end;
    });
}

export default function Availability({ user }) {
    const [tab, setTab] = useState('fleet');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selDate, setSelDate] = useState(new Date().toISOString().split('T')[0]);
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vendorFleet, setVendorFleet] = useState([]);
    const [vendorDrvrs, setVendorDrvrs] = useState([]);
    const [trips, setTrips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const PER_PAGE = 20;

    const operatorId = user?.operatorId || user?._id || 'all';

    useEffect(() => { fetchAll(); }, []);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [vRes, dRes, vfRes, tRes] = await Promise.all([
                axios.get(`${API}/vehicles/${operatorId}`),
                axios.get(`${API}/drivers/${operatorId}`),
                axios.get(`${API}/vendor-fleet/${operatorId}`),
                axios.get(`${API}/trips/${operatorId}`),
            ]);
            setVehicles(vRes.data || []);
            const allDrivers = dRes.data || [];
            setDrivers(allDrivers.filter(d => !d.vendorId));
            setVendorDrvrs(allDrivers.filter(d => d.vendorId));
            setVendorFleet(vfRes.data || []);
            setTrips(tRes.data || []);
        } catch (err) { console.error(err); }
        setLoading(false);
    };

    const dayTrips = trips.filter(t => {
        if (t.tripDate) return t.tripDate.startsWith(selDate);
        if (t.tripFrequency === 'Recurring' && t.recurringDays?.length) {
            const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(selDate + 'T00:00:00').getDay()];
            return t.recurringDays.includes(dow);
        }
        return true;
    });

    const tabCounts = {
        fleet: vehicles.length, drivers: drivers.length,
        vfleet: vendorFleet.length, vdrivers: vendorDrvrs.length,
    };

    const allRows = (() => {
        const q = search.toLowerCase();
        let list = [];
        if (tab === 'fleet')
            list = vehicles.filter(v => (v.registrationNumber + ' ' + (v.make || '')).toLowerCase().includes(q))
                .map(v => ({ id: v._id, name: v.registrationNumber || '—', sub: [v.make, v.model].filter(Boolean).join(' '), raw: v }));
        else if (tab === 'drivers')
            list = drivers.filter(d => (d.name || '').toLowerCase().includes(q) || (d.phoneNumber || '').includes(q))
                .map(d => ({ id: d._id, name: d.name || '—', sub: d.phoneNumber || '', raw: d }));
        else if (tab === 'vfleet')
            list = vendorFleet.filter(f => (f.registrationNumber || '').toLowerCase().includes(q) || (f.vehicleType || '').toLowerCase().includes(q))
                .map(f => ({ id: f._id, name: f.registrationNumber || '—', sub: f.vehicleType || f.make || '', raw: f }));
        else
            list = vendorDrvrs.filter(d => (d.name || '').toLowerCase().includes(q) || (d.phoneNumber || '').includes(q))
                .map(d => ({ id: d._id, name: d.name || '—', sub: d.phoneNumber || '', raw: d }));

        if (statusFilter !== 'all') {
            return list.filter(r => {
                const leave = getLeaveAtDate(r.raw, selDate);
                if (statusFilter === 'Absent') return !!leave;
                if (leave) return false;
                const trip = HOURS.some(h => {
                    const t = getTripAtHour(r.id, h, dayTrips);
                    return t && (t.billingType === statusFilter || (statusFilter === 'On Trip' && t));
                });
                if (statusFilter === 'Available') return !trip;
                return trip;
            });
        }
        return list;
    })();

    const totalPages = Math.max(1, Math.ceil(allRows.length / PER_PAGE));
    const rows = allRows.slice((page - 1) * PER_PAGE, page * PER_PAGE);
    const totalRows = allRows.length;
    const currentHour = new Date().getHours();
    const busyEntities = allRows.filter(r => !!getTripAtHour(r.id, currentHour, dayTrips));
    const onTripCount = busyEntities.length;
    const availCount = allRows.filter(r => !getTripAtHour(r.id, currentHour, dayTrips) && !getLeaveAtDate(r.raw, selDate)).length;

    const today = new Date().toISOString().split('T')[0];
    const isToday = selDate === today;
    const displayDate = new Date(selDate + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

    const activeTab = TABS.find(t => t.key === tab);

    return (
        <div style={{ minHeight: '100vh', background: '#f3f4f8', fontFamily: FONT }}>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse2 { 0%,100%{opacity:.6} 50%{opacity:1} }
        .av-tab-btn { transition: all .15s ease; }
        .av-tab-btn:hover { background: #f1f5f9 !important; }
        .av-cell { transition: transform .12s ease, box-shadow .12s ease; cursor: default; }
        .av-cell:hover { transform: scale(1.3); z-index: 5; position: relative; }
        .av-row:hover { background: #f8fafc !important; }
        .av-pg:hover:not(:disabled) { background: #e0e7ff !important; color: #4f46e5 !important; border-color: #c7d2fe !important; }
        .av-refresh:hover { background: rgba(255,255,255,.25) !important; transform: rotate(30deg); }
        .av-refresh { transition: transform .3s ease, background .2s; }
        .av-date-input { color-scheme: dark; }
        .av-date-input::-webkit-calendar-picker-indicator { filter: invert(1) opacity(.6); cursor: pointer; }
      `}</style>

            {/* ══ HERO BANNER ══════════════════════════════════════ */}
            <div style={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 55%, #312e81 100%)',
                padding: '32px 40px 28px',
                position: 'relative',
                overflow: 'hidden',
            }}>
                {/* Decorative blobs */}
                <div style={{ position: 'absolute', top: -80, right: 60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(99,102,241,.12)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: -60, right: 400, width: 180, height: 180, borderRadius: '50%', background: 'rgba(139,92,246,.1)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', top: 10, left: 440, width: 120, height: 120, borderRadius: '50%', background: 'rgba(56,189,248,.06)', pointerEvents: 'none' }} />

                <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
                    {/* Left: breadcrumb + title */}
                    <div>
                        <div style={{ fontSize: 10.5, fontWeight: 700, color: 'rgba(255,255,255,.45)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>
                            Trip Operations &nbsp;/&nbsp; Availability
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                            <div style={{ width: 48, height: 48, borderRadius: 16, background: 'rgba(99,102,241,.25)', border: '1px solid rgba(99,102,241,.4)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon d={IC.calendar} size={22} color="#a5b4fc" sw={2} />
                            </div>
                            <div>
                                <h1 style={{ margin: 0, fontSize: 26, fontWeight: 900, color: 'white', letterSpacing: '-0.02em' }}>Availability</h1>
                                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'rgba(255,255,255,.45)', fontWeight: 500 }}>
                                    Fleet · Drivers · Vendor Fleet · Vendor Drivers — 24-hour heatmap view
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Right: date picker + refresh */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <label
                            style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,.07)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,.15)', borderRadius: 14, padding: '10px 16px', cursor: 'pointer' }}
                        >
                            <Icon d={IC.calendar} size={14} color="rgba(255,255,255,.6)" />
                            <input
                                id="av-date"
                                type="date"
                                className="av-date-input"
                                value={selDate}
                                onChange={e => { setSelDate(e.target.value); setPage(1); }}
                                onClick={e => {
                                    if (e.target.showPicker) {
                                        try { e.target.showPicker(); } catch (err) { /* ignore gesture issues */ }
                                    }
                                }}
                                style={{ background: 'transparent', border: 'none', outline: 'none', color: 'white', fontFamily: FONT, fontSize: 13.5, fontWeight: 700, cursor: 'pointer', minWidth: 130 }}
                            />
                            <span style={{
                                padding: '3px 10px', borderRadius: 20, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.04em',
                                background: isToday ? 'rgba(99,102,241,.5)' : 'rgba(245,158,11,.45)',
                                color: isToday ? '#c7d2fe' : '#fde68a',
                                border: `1px solid ${isToday ? 'rgba(99,102,241,.5)' : 'rgba(245,158,11,.4)'}`,
                            }}>
                                {isToday ? 'TODAY' : displayDate.split(',')[0].toUpperCase()}
                            </span>
                        </label>
                        <button onClick={fetchAll} className="av-refresh" title="Refresh data" style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.15)', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon d={IC.refresh} size={16} color="rgba(255,255,255,.8)" sw={2} />
                        </button>
                    </div>
                </div>

                {/* ── Stat pills in banner ── */}
                <div style={{ position: 'relative', zIndex: 2, display: 'flex', gap: 12, marginTop: 24, flexWrap: 'wrap' }}>
                    {[
                        { label: 'Total', val: totalRows, bg: 'rgba(99,102,241,.2)', border: 'rgba(99,102,241,.35)', num: '#c7d2fe', key: 'all' },
                        { label: 'Available Now', val: availCount, bg: 'rgba(34,197,94,.15)', border: 'rgba(34,197,94,.3)', num: '#86efac', key: 'Available' },
                        { label: 'On Trips Now', val: onTripCount, bg: 'rgba(249,115,22,.15)', border: 'rgba(249,115,22,.3)', num: '#fb923c', key: 'On Trip' },
                        { label: 'Absent Today', val: allRows.filter(r => getLeaveAtDate(r.raw, selDate)).length, bg: 'rgba(239,68,68,.15)', border: 'rgba(239,68,68,.3)', num: '#fca5a5', key: 'Absent' },
                    ].map(s => (
                        <div 
                            key={s.label} 
                            onClick={() => { setStatusFilter(s.key); setPage(1); }}
                            style={{ 
                                background: s.bg, border: `1px solid ${s.border}`, borderRadius: 12, padding: '10px 20px', 
                                display: 'flex', alignItems: 'center', gap: 10, backdropFilter: 'blur(8px)', cursor: 'pointer',
                                transition: 'transform .1s ease, box-shadow .1s ease',
                                transform: statusFilter === s.key ? 'scale(1.05)' : 'none',
                                boxShadow: statusFilter === s.key ? `0 0 15px ${s.border}` : 'none'
                            }}
                        >
                            <span style={{ fontSize: 22, fontWeight: 900, color: s.num, lineHeight: 1 }}>{s.val}</span>
                            <span style={{ fontSize: 11.5, fontWeight: 700, color: 'rgba(255,255,255,.55)' }}>{s.label}</span>
                        </div>
                    ))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto', paddingRight: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {Object.entries(COLORS).map(([l, c]) => (
                            <div 
                                key={l} 
                                onClick={() => { setStatusFilter(l === 'Available' ? 'Available' : (l === 'Absent' ? 'Absent' : l)); setPage(1); }}
                                style={{ 
                                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, 
                                    color: statusFilter === (l === 'Available' ? 'Available' : (l === 'Absent' ? 'Absent' : l)) ? 'white' : 'rgba(255,255,255,0.65)', 
                                    cursor: 'pointer', transition: 'color .2s',
                                    background: statusFilter === (l === 'Available' ? 'Available' : (l === 'Absent' ? 'Absent' : l)) ? 'rgba(255,255,255,0.1)' : 'transparent',
                                    padding: '4px 8px', borderRadius: 8
                                }}
                            >
                                <div style={{ width: 10, height: 10, borderRadius: 3, background: c.bg.split(',')[1] }} />{l}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ══ TABS ═════════════════════════════════════════════ */}
            <div style={{ background: 'white', borderBottom: '2px solid #f1f5f9', padding: '0 40px', display: 'flex', gap: 0, boxShadow: '0 2px 8px rgba(0,0,0,.04)' }}>
                {TABS.map(t => {
                    const active = t.key === tab;
                    return (
                        <button key={t.key} className="av-tab-btn"
                            onClick={() => { setTab(t.key); setSearch(''); setPage(1); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '15px 24px', border: 'none', borderBottom: active ? `3px solid ${t.color}` : '3px solid transparent', background: 'transparent', fontFamily: FONT, fontWeight: active ? 800 : 600, fontSize: 13.5, color: active ? t.color : '#94a3b8', cursor: 'pointer', transition: 'all .15s', marginBottom: -2 }}>
                            <Icon d={IC[t.icon]} size={15} color={active ? t.color : '#cbd5e1'} sw={2} />
                            {t.label}
                            <span style={{ background: active ? t.color + '18' : '#f1f5f9', color: active ? t.color : '#94a3b8', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 800 }}>
                                {tabCounts[t.key]}
                            </span>
                        </button>
                    );
                })}

                {/* Search — at far right in tab bar */}
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <select 
                        value={statusFilter} 
                        onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                        style={{ height: 38, borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', padding: '0 12px', fontSize: 13, fontWeight: 600, color: '#475569', outline: 'none', cursor: 'pointer' }}
                    >
                        <option value="all">All Status</option>
                        <option value="Available">Available Only</option>
                        <option value="Absent">Absent Only</option>
                        <option value="Daily">Daily Trips</option>
                        <option value="Monthly">Monthly Trips</option>
                        <option value="Shuttle">Shuttle Trips</option>
                        <option value="Shuttle with Booking">Shuttle with Booking</option>
                        <option value="Ad-hoc">Ad-hoc Trips</option>
                    </select>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '0 14px', height: 38, minWidth: 200 }}>
                        <Icon d={IC.search} size={13} color="#94a3b8" />
                        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                            placeholder={`Search ${activeTab?.label}...`}
                            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, fontFamily: FONT, background: 'transparent', color: '#334155' }} />
                    </div>
                </div>
            </div>

            {/* ══ TABLE ════════════════════════════════════════════ */}
            <div style={{ padding: '28px 40px 40px', animation: 'fadeUp .3s ease' }}>
                <div style={{ background: 'white', borderRadius: 20, border: '1px solid #e8eaf0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(15,23,42,.07)' }}>

                    {loading ? (
                        <div style={{ padding: '70px 40px', textAlign: 'center' }}>
                            <div style={{ width: 44, height: 44, borderRadius: '50%', border: `3px solid #e0e7ff`, borderTopColor: '#6366f1', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite' }} />
                            <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
                            <div style={{ fontSize: 14, color: '#94a3b8', fontWeight: 600 }}>Loading availability data…</div>
                        </div>
                    ) : rows.length === 0 ? (
                        <div style={{ padding: '70px 40px', textAlign: 'center' }}>
                            <div style={{ width: 72, height: 72, borderRadius: 20, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                <Icon d={IC[activeTab?.icon]} size={32} color="#94a3b8" sw={1.5} />
                            </div>
                            <div style={{ fontWeight: 800, fontSize: 16, color: '#1e293b', marginBottom: 6 }}>No {activeTab?.label} found</div>
                            <div style={{ fontSize: 13, color: '#94a3b8' }}>{search ? 'Try a different search term' : 'No records available yet'}</div>
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                                <thead>
                                    <tr style={{ background: 'linear-gradient(90deg,#f8fafc,#f1f5f9)', borderBottom: '2px solid #e8eaf0' }}>
                                        <th style={{ padding: '14px 20px', fontSize: 11, fontWeight: 800, color: '#64748b', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap', minWidth: 200 }}>
                                            {activeTab?.label}
                                        </th>
                                        <th style={{ padding: '14px 12px', fontSize: 11, fontWeight: 800, color: '#64748b', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap', width: 90 }}>
                                            Hours
                                        </th>
                                        {HOURS.map(h => (
                                            <th key={h} style={{ padding: '8px 1px', minWidth: 28, textAlign: 'center' }}>
                                                <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', lineHeight: 1.1 }}>
                                                    {h === 0 ? '12' : h > 12 ? h - 12 : h}
                                                </div>
                                                <div style={{ fontSize: 7.5, color: '#cbd5e1', fontWeight: 600, letterSpacing: '0.03em' }}>
                                                    {h < 12 ? 'AM' : 'PM'}
                                                </div>
                                            </th>
                                        ))}
                                        <th style={{ padding: '14px 20px', fontSize: 11, fontWeight: 800, color: '#64748b', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap', width: 110 }}>
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(r => {
                                        const leave = getLeaveAtDate(r.raw, selDate);
                                        const busyHours = HOURS.map(h => getTripAtHour(r.id, h, dayTrips)).filter(Boolean);
                                        const isAvail = !leave && busyHours.length === 0;
                                        const isMonthly = busyHours.some(t => t.billingType === 'Monthly');
                                        
                                        const statusKey = leave ? 'Absent' : (isMonthly ? 'Monthly' : (isAvail ? 'Available' : 'Daily'));
                                        const sc = COLORS[statusKey];

                                        return (
                                            <tr key={r.id} className="av-row" style={{ borderBottom: '1px solid #f3f4f7', transition: 'background .12s' }}>
                                                {/* Name cell */}
                                                <td style={{ padding: '11px 20px', whiteSpace: 'nowrap' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                                                        <div style={{ 
                                                            width: 34, height: 34, borderRadius: 10, 
                                                            background: sc.bg.split(',')[1].replace(')', ''), 
                                                            opacity: 0.15,
                                                            border: `1.5px solid ${sc.border}`, 
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                                            position: 'relative'
                                                        }}>
                                                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <Icon d={IC[activeTab?.icon]} size={16} color={sc.text} sw={2} />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                {r.name}
                                                                {isMonthly && (
                                                                    <span style={{ fontSize: 9, fontWeight: 800, background: COLORS.Monthly.text, color: 'white', padding: '1px 5px', borderRadius: 4, textTransform: 'uppercase' }}>Monthly</span>
                                                                )}
                                                                {leave && (
                                                                    <span style={{ fontSize: 9, fontWeight: 800, background: COLORS.Absent.text, color: 'white', padding: '1px 5px', borderRadius: 4, textTransform: 'uppercase' }}>Absent</span>
                                                                )}
                                                            </div>
                                                            {r.sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1, fontWeight: 500 }}>{r.sub}</div>}
                                                        </div>
                                                    </div>
                                                </td>
                                                {/* Hours busy */}
                                                <td style={{ padding: '11px 12px', fontSize: 12.5, fontWeight: 700, color: sc.text, whiteSpace: 'nowrap' }}>
                                                    {leave ? (leave.leaveType || 'On Leave') : (isMonthly ? 'Dedicated (Monthly)' : (busyHours.length > 0 ? `${busyHours.length}h busy` : 'Free all day'))}
                                                </td>
                                                {/* Heat cells */}
                                                {HOURS.map(h => {
                                                    const trip = getTripAtHour(r.id, h, dayTrips);
                                                    const busy = !!trip;
                                                    const type = trip?.billingType || (leave ? 'Absent' : 'Available');
                                                    const style = COLORS[type] || COLORS.Daily;

                                                    const tooltip = leave ? 
                                                        `Absent: ${leave.leaveType || 'On Leave'}\nStatus: Approved` : 
                                                        (busy ? `${fmtHour(h)}: ${trip.clientId?.companyName || 'Trip'} (${type}${trip.tripType ? ` / ${trip.tripType}` : ''})\nRoute: ${trip.routeId?.name || 'Unspecified'}` : `${fmtHour(h)}: Available`);

                                                    return (
                                                        <td key={h} style={{ padding: '6px 1px', textAlign: 'center' }}>
                                                            <div className="av-cell" title={tooltip}
                                                                style={{
                                                                    width: 22, height: 22, borderRadius: 5, margin: '0 auto',
                                                                    background: style.bg,
                                                                    border: `1.5px solid ${style.border}`,
                                                                    boxShadow: `0 1px 4px ${style.shadow}`,
                                                                }}
                                                            />
                                                        </td>
                                                    );
                                                })}
                                                {/* Status pill */}
                                                <td style={{ padding: '11px 20px', textAlign: 'center' }}>
                                                    <span style={{ 
                                                        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 13px', borderRadius: 20, fontSize: 11.5, fontWeight: 700, 
                                                        background: sc.bg.split(',')[1].replace(')', '') + '20', 
                                                        color: sc.text, 
                                                        border: `1px solid ${sc.border}60` 
                                                    }}>
                                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: sc.text, display: 'inline-block', animation: isAvail ? 'none' : 'pulse2 1.5s infinite' }} />
                                                        {leave ? 'Absent' : (isMonthly ? 'Monthly' : (isAvail ? 'Available' : 'On Trip'))}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ── Pagination footer ── */}
                    {!loading && allRows.length > 0 && (
                        <div style={{ padding: '14px 20px', borderTop: '1px solid #f3f4f7', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafbfc' }}>
                            <div style={{ fontSize: 12.5, color: '#94a3b8', fontWeight: 600 }}>
                                Showing <strong style={{ color: '#64748b' }}>{(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, allRows.length)}</strong> of <strong style={{ color: '#64748b' }}>{allRows.length}</strong> {activeTab?.label}
                            </div>
                            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="av-pg"
                                    style={{ width: 32, height: 32, borderRadius: 9, border: '1.5px solid #e2e8f0', background: 'white', color: page === 1 ? '#e2e8f0' : '#475569', fontFamily: FONT, cursor: page === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}>
                                    <Icon d={IC.chevronL} size={14} color={page === 1 ? '#e2e8f0' : '#475569'} sw={2.5} />
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(p => Math.abs(p - page) <= 2)
                                    .map(p => (
                                        <button key={p} onClick={() => setPage(p)} className="av-pg"
                                            style={{ width: 32, height: 32, borderRadius: 9, border: `1.5px solid ${p === page ? '#6366f1' : '#e2e8f0'}`, background: p === page ? '#6366f1' : 'white', color: p === page ? 'white' : '#475569', fontFamily: FONT, fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all .15s' }}>
                                            {p}
                                        </button>
                                    ))
                                }
                                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="av-pg"
                                    style={{ width: 32, height: 32, borderRadius: 9, border: '1.5px solid #e2e8f0', background: 'white', color: page === totalPages ? '#e2e8f0' : '#475569', fontFamily: FONT, cursor: page === totalPages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}>
                                    <Icon d={IC.chevronR} size={14} color={page === totalPages ? '#e2e8f0' : '#475569'} sw={2.5} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
